// src/components/GlobalSearch.tsx
import React, { useState, useRef, useEffect } from 'react';
import { AutoComplete, Space, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';

const { Text } = Typography;

interface SearchResult {
  type: 'diary' | 'chart' | 'page';
  title: string;
  path: string;
  description?: string;
}

export const GlobalSearch: React.FC = () => {
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const searchRef = useRef<any>(null);

  const staticOptions: SearchResult[] = [
    { type: 'page', title: 'Главная', path: '/dashboard', description: 'Дашборд с обзором' },
    { type: 'diary', title: 'Дневник глюкозы', path: '/diary/glucose', description: 'Записи уровня сахара' },
    { type: 'diary', title: 'Дневник питания', path: '/diary/nutrition', description: 'Записи о питании' },
    { type: 'diary', title: 'Дневник веса', path: '/diary/weight', description: 'Отслеживание веса' },
    { type: 'diary', title: 'Дневник симптомов', path: '/diary/symptoms', description: 'Записи симптомов' },
    { type: 'page', title: 'Графики', path: '/charts', description: 'Визуализация данных' },
    { type: 'page', title: 'Календарь', path: '/calendar', description: 'Календарь событий' },
    { type: 'page', title: 'Чат', path: '/chat', description: 'Общение с врачом' },
    { type: 'page', title: 'Профиль', path: '/profile', description: 'Настройки профиля' },
  ];

  if (user?.role === 'doctor' || user?.role === 'admin') {
    staticOptions.push(
      { type: 'page', title: 'Пациенты', path: '/doctors', description: 'Список пациентов' }
    );
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSearch = (value: string) => {
    setSearchValue(value);
    if (!value) {
      setOptions([]);
      return;
    }

    const filtered = staticOptions
      .filter(
        (item) =>
          item.title.toLowerCase().includes(value.toLowerCase()) ||
          item.description?.toLowerCase().includes(value.toLowerCase())
      )
      .map((item) => ({
        value: item.title,
        label: (
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <Text strong>{item.title}</Text>
            {item.description && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {item.description}
              </Text>
            )}
          </Space>
        ),
        path: item.path,
      }));

    setOptions(filtered);
    setOpen(filtered.length > 0);
  };

  const handleSelect = (value: string, option: any) => {
    if (option.path) {
      navigate(option.path);
      setSearchValue('');
      setOpen(false);
    }
  };

  return (
    <AutoComplete
      ref={searchRef}
      value={searchValue}
      options={options}
      onSearch={handleSearch}
      onSelect={handleSelect}
      open={open}
      onBlur={() => setTimeout(() => setOpen(false), 200)}
      onFocus={() => searchValue && setOpen(true)}
      style={{ width: '100%', maxWidth: 400 }}
      placeholder="Поиск (Ctrl+K)"
      prefix={<SearchOutlined />}
      allowClear
    />
  );
};
